import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Plus, X, Pencil, Trash2, DollarSign, FileText, Clock,
  AlertCircle, CheckCircle, Search, ChevronDown, ChevronUp,
  CreditCard, Calendar, ChevronRight,
} from 'lucide-react';

// âââ Constants ââââââââââââââââââââââââââââââââââââââââââââââââââââ
export const CURRENCIES = {
  USD: { symbol: '$',    name: 'US Dollar',       flag: '' },
  EGP: { symbol: 'EGP ', name: 'Egyptian Pound',   flag: '' },
  EUR: { symbol: 'EUR ',   name: 'Euro',             flag: '' },
  SAR: { symbol: 'SAR ', name: 'Saudi Riyal',      flag: '' },
  AED: { symbol: 'AED ', name: 'UAE Dirham',       flag: '' },
};

export const fmtCurrency = (amount, currency = 'USD') => {
  const c = CURRENCIES[currency] || CURRENCIES.USD;
  const num = Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${c.symbol}${num}`;
};

const statusColor = {
  draft: 'badge-gray', sent: 'badge-blue', paid: 'badge-green',
  overdue: 'badge-red', cancelled: 'badge-gray',
};
const instStatusColor = {
  pending: 'badge-blue', paid: 'badge-green', overdue: 'badge-red',
};

const today = () => new Date().toISOString().split('T')[0];
const daysUntil = (dateStr) => {
  const due = new Date(dateStr); due.setHours(0, 0, 0, 0);
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((due - now) / (1000 * 60 * 60 * 24));
};

const groupedMoney = (items, status) => {
  const groups = {};
  items.filter(i => i.status === status).forEach(i => {
    const code = i.currency || 'USD';
    groups[code] = (groups[code] || 0) + parseFloat(i.total || 0);
  });
  return Object.entries(groups).map(([currency, amount]) => fmtCurrency(amount, currency)).join(' / ') || fmtCurrency(0, 'USD');
};

// âââ Line Items Editor ââââââââââââââââââââââââââââââââââââââââââââ
const LineItems = ({ items, onChange, readOnly, currency }) => {
  const addRow = () => onChange([...items, { description: '', qty: 1, unitPrice: '' }]);
  const update = (i, k, v) => onChange(items.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const rowTotal = (row) => ((parseFloat(row.qty) || 0) * (parseFloat(row.unitPrice) || 0)).toFixed(2);
  const sym = CURRENCIES[currency]?.symbol || '$';

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 110px 100px 28px', gap: 6, marginBottom: 6 }}>
        {['Description', 'Qty', 'Unit Price', 'Total', ''].map(h => (
          <div key={h} style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', padding: '0 2px' }}>{h}</div>
        ))}
      </div>
      {items.map((row, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 110px 100px 28px', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <input className="input" style={{ fontSize: 13, padding: '8px 10px' }} value={row.description} disabled={readOnly}
            onChange={e => update(i, 'description', e.target.value)} placeholder="Item description..." />
          <input className="input" style={{ fontSize: 13, padding: '8px 10px' }} type="number" min="1" value={row.qty} disabled={readOnly}
            onChange={e => update(i, 'qty', e.target.value)} />
          <input className="input" style={{ fontSize: 13, padding: '8px 10px' }} type="number" min="0" step="0.01" value={row.unitPrice} disabled={readOnly}
            onChange={e => update(i, 'unitPrice', e.target.value)} placeholder="0.00" />
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', paddingLeft: 4 }}>{sym}{rowTotal(row)}</div>
          {!readOnly ? (
            <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4 }}><X size={14} /></button>
          ) : <div />}
        </div>
      ))}
      {!readOnly && (
        <button onClick={addRow} className="btn btn-secondary btn-sm" style={{ marginTop: 4 }}><Plus size={13} /> Add Line</button>
      )}
    </div>
  );
};

// âââ Installments Editor ââââââââââââââââââââââââââââââââââââââââââ
const InstallmentsEditor = ({ installments, onChange, readOnly, currency, invoiceTotal }) => {
  const sym = CURRENCIES[currency]?.symbol || '$';
  const totalInstallments = installments.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const diff = invoiceTotal - totalInstallments;

  const add = () => onChange([...installments, { description: `Installment ${installments.length + 1}`, amount: '', dueDate: '' }]);
  const remove = (i) => onChange(installments.filter((_, idx) => idx !== i));
  const update = (i, k, v) => onChange(installments.map((row, idx) => idx === i ? { ...row, [k]: v } : row));

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px 28px', gap: 8, marginBottom: 8 }}>
        {['Description', 'Amount', 'Due Date', ''].map(h => (
          <div key={h} style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>{h}</div>
        ))}
      </div>

      {installments.map((inst, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px 28px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          {readOnly ? (
            <>
              <div style={{ fontSize: 13 }}>{inst.description}</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{sym}{Number(inst.amount).toLocaleString()}</div>
              <div style={{ fontSize: 13 }}>{inst.dueDate ? new Date(inst.dueDate).toLocaleDateString() : ''}</div>
              <div />
            </>
          ) : (
            <>
              <input className="input" style={{ fontSize: 13, padding: '8px 10px' }} value={inst.description}
                onChange={e => update(i, 'description', e.target.value)} placeholder={`Installment ${i + 1}`} />
              <input className="input" style={{ fontSize: 13, padding: '8px 10px' }} type="number" min="0" step="0.01"
                value={inst.amount} onChange={e => update(i, 'amount', e.target.value)} placeholder="0.00" />
              <input className="input" style={{ fontSize: 13, padding: '8px 10px' }} type="date"
                value={inst.dueDate} onChange={e => update(i, 'dueDate', e.target.value)} />
              <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4 }}><X size={14} /></button>
            </>
          )}
        </div>
      ))}

      {!readOnly && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <button onClick={add} className="btn btn-secondary btn-sm"><Plus size={13} /> Add Installment</button>
          {installments.length > 0 && (
            <div style={{ fontSize: 12, color: Math.abs(diff) < 0.01 ? '#22c55e' : '#eab308' }}>
              {Math.abs(diff) < 0.01 ? '? Installments match total' : `Difference: ${sym}${Math.abs(diff).toFixed(2)} ${diff > 0 ? 'remaining' : 'over'}`}
            </div>
          )}
        </div>
      )}

      {/* Total bar */}
      {installments.length > 0 && (
        <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span style={{ color: 'var(--text-muted)' }}>Total Installments</span>
          <span style={{ fontWeight: 700 }}>{sym}{totalInstallments.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>
      )}
    </div>
  );
};

// âââ Installment Status View (inside modal) âââââââââââââââââââââââ
const InstallmentList = ({ installments, invoiceId, currency, canManage, onPaid }) => {
  const sym = CURRENCIES[currency]?.symbol || '$';
  const paid = installments.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0);
  const total = installments.reduce((s, i) => s + i.amount, 0);
  const pct = total > 0 ? Math.round((paid / total) * 100) : 0;

  const markPaid = async (idx) => {
    if (!window.confirm(`Mark installment "${installments[idx].description}" as paid?`)) return;
    try {
      await axios.put(`/api/invoices/${invoiceId}/installments/${idx}/pay`);
      onPaid();
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div>
      {/* Progress bar */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
          <span>Collected</span>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{sym}{paid.toLocaleString('en-US', { minimumFractionDigits: 2 })} / {sym}{total.toLocaleString('en-US', { minimumFractionDigits: 2 })} ({pct}%)</span>
        </div>
        <div className="progress-bar" style={{ height: 8 }}>
          <div className="progress-fill" style={{ width: `${pct}%`, background: pct === 100 ? '#22c55e' : '#a855f7' }} />
        </div>
      </div>

      {/* Installment rows */}
      {installments.map((inst, i) => {
        const days = inst.dueDate ? daysUntil(inst.dueDate) : null;
        const isOverdue = days !== null && days < 0 && inst.status !== 'paid';
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
            borderRadius: 8, marginBottom: 8,
            background: inst.status === 'paid' ? 'rgba(34,197,94,0.07)' : isOverdue ? 'rgba(239,68,68,0.07)' : 'var(--bg-secondary)',
            border: `1px solid ${inst.status === 'paid' ? 'rgba(34,197,94,0.25)' : isOverdue ? 'rgba(239,68,68,0.25)' : 'var(--border)'}`,
          }}>
            {/* Icon */}
            <div style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              background: inst.status === 'paid' ? 'rgba(34,197,94,0.15)' : isOverdue ? 'rgba(239,68,68,0.15)' : 'var(--bg-hover)' }}>
              {inst.status === 'paid' ? <CheckCircle size={16} color="#22c55e" /> : isOverdue ? <AlertCircle size={16} color="#ef4444" /> : <Clock size={16} color="#3b82f6" />}
            </div>

            {/* Info */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{inst.description}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 10 }}>
                {inst.dueDate && (
                  <span style={{ color: isOverdue ? '#ef4444' : 'inherit' }}>
                    {isOverdue ? `Overdue by ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''}` : days === 0 ? 'Due today' : `Due in ${days} day${days !== 1 ? 's' : ''}`} · {new Date(inst.dueDate).toLocaleDateString()}
                  </span>
                )}
                {inst.paidAt && <span style={{ color: '#22c55e' }}>Paid {new Date(inst.paidAt).toLocaleDateString()}</span>}
              </div>
            </div>

            {/* Amount */}
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{sym}{Number(inst.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              <span className={`badge ${instStatusColor[inst.status] || 'badge-gray'}`} style={{ fontSize: 10, marginTop: 4 }}>{inst.status}</span>
            </div>

            {/* Action */}
            {canManage && inst.status !== 'paid' && (
              <button onClick={() => markPaid(i)} className="btn btn-secondary btn-sm" style={{ flexShrink: 0, fontSize: 12 }}>
                <CheckCircle size={12} /> Mark Paid
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

// âââ Invoice Modal ââââââââââââââââââââââââââââââââââââââââââââââââ
const InvoiceModal = ({ onClose, onSave, clients, projects, initial, viewOnly, canManage }) => {
  const isEdit = !!initial;
  const [activeTab, setActiveTab] = useState('details'); // details | installments
  const safeArr = (v) => Array.isArray(v) ? v : (typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch { return []; } })() : []);
  const [form, setForm] = useState(
    initial ? {
      clientId: initial.clientId || '',
      projectId: initial.projectId || '',
      currency: initial.currency || 'USD',
      status: initial.status || 'draft',
      issueDate: initial.issueDate ? initial.issueDate.split('T')[0] : today(),
      dueDate: initial.dueDate ? initial.dueDate.split('T')[0] : '',
      taxRate: initial.taxRate || 0,
      notes: initial.notes || '',
      items: safeArr(initial.items).length ? safeArr(initial.items) : [{ description: '', qty: 1, unitPrice: '' }],
      installments: safeArr(initial.installments),
    } : {
      clientId: '', projectId: '', currency: 'USD', status: 'draft',
      issueDate: today(), dueDate: '', taxRate: 0, notes: '',
      items: [{ description: '', qty: 1, unitPrice: '' }],
      installments: [],
    }
  );
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const subtotal = form.items.reduce((s, r) => s + (parseFloat(r.qty) || 0) * (parseFloat(r.unitPrice) || 0), 0);
  const tax = subtotal * (parseFloat(form.taxRate) / 100);
  const itemsTotal = subtotal + tax;
  const hasInstallments = form.installments.length > 0;
  const invoiceTotal = hasInstallments
    ? form.installments.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
    : itemsTotal;

  const sym = CURRENCIES[form.currency]?.symbol || '$';

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (isEdit) await axios.put(`/api/invoices/${initial.id}`, payload);
      else await axios.post('/api/invoices', payload);
      onSave();
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const tabs = [
    { key: 'details', label: 'Invoice Details' },
    { key: 'installments', label: `Payment Plan${form.installments.length ? ` (${form.installments.length})` : ''}` },
  ];

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 740, maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">
              {viewOnly ? initial?.invoiceNumber : isEdit ? `Edit ${initial?.invoiceNumber}` : 'New Invoice'}
            </div>
            {initial?.invoiceNumber && !viewOnly && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{initial.invoiceNumber}</div>
            )}
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 24px' }}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ background: 'none', border: 'none', padding: '12px 16px', fontSize: 13, fontWeight: activeTab === tab.key ? 600 : 400,
                color: activeTab === tab.key ? 'var(--accent)' : 'var(--text-muted)', cursor: 'pointer',
                borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom: -1, transition: 'all 0.15s' }}>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {activeTab === 'details' && (
              <>
                {/* Currency */}
                <div className="input-group">
                  <label className="input-label">Currency</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {Object.entries(CURRENCIES).map(([code, c]) => (
                      <button key={code} type="button" disabled={viewOnly}
                        onClick={() => set('currency', code)}
                        style={{
                          padding: '8px 14px', borderRadius: 8, border: `1.5px solid ${form.currency === code ? 'var(--accent)' : 'var(--border)'}`,
                          background: form.currency === code ? 'var(--accent-dim)' : 'var(--bg-secondary)',
                          color: form.currency === code ? 'var(--accent)' : 'var(--text-muted)',
                          cursor: viewOnly ? 'default' : 'pointer', fontSize: 13, fontWeight: form.currency === code ? 600 : 400,
                          display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
                        }}>
                        <span>{c.flag}</span> {code} <span style={{ opacity: 0.7 }}>({c.symbol})</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Client & Project */}
                <div className="grid-2">
                  <div className="input-group">
                    <label className="input-label">Client *</label>
                    <select className="input select" required value={form.clientId} onChange={e => set('clientId', e.target.value)} disabled={viewOnly}>
                      <option value="">Select client...</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                    </select>
                  </div>
                  <div className="input-group">
                    <label className="input-label">Project</label>
                    <select className="input select" value={form.projectId} onChange={e => set('projectId', e.target.value)} disabled={viewOnly}>
                      <option value=""> None </option>
                      {projects.filter(p => !form.clientId || p.clientId === form.clientId).map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Dates & Status */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="input-group">
                    <label className="input-label">Issue Date *</label>
                    <input type="date" className="input" required value={form.issueDate} onChange={e => set('issueDate', e.target.value)} disabled={viewOnly} />
                  </div>
                  <div className="input-group">
                    <label className="input-label">Due Date *</label>
                    <input type="date" className="input" required value={form.dueDate} onChange={e => set('dueDate', e.target.value)} disabled={viewOnly} />
                  </div>
                  <div className="input-group">
                    <label className="input-label">Status</label>
                    <select className="input select" value={form.status} onChange={e => set('status', e.target.value)} disabled={viewOnly}>
                      {['draft', 'sent', 'paid', 'overdue', 'cancelled'].map(s => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Line Items */}
                <div className="input-group">
                  <label className="input-label">Line Items</label>
                  <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
                    <LineItems items={form.items} onChange={v => set('items', v)} readOnly={viewOnly} currency={form.currency} />
                  </div>
                </div>

                {/* Tax & Totals */}
                {!hasInstallments && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ minWidth: 260 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Tax %</span>
                          {!viewOnly ? (
                            <input type="number" className="input" min="0" max="100" step="0.01"
                              style={{ width: 70, padding: '4px 8px', fontSize: 13 }}
                              value={form.taxRate} onChange={e => set('taxRate', e.target.value)} />
                          ) : <span style={{ fontSize: 13 }}>{form.taxRate}%</span>}
                        </div>
                      </div>
                      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
                          <span>Subtotal</span><span>{sym}{subtotal.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
                          <span>Tax ({form.taxRate}%)</span><span>{sym}{tax.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, marginTop: 8 }}>
                          <span>Total</span><span style={{ color: 'var(--accent)' }}>{sym}{itemsTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {hasInstallments && (
                  <div style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700 }}>
                    <span>Invoice Total (from installments)</span>
                    <span style={{ color: 'var(--accent)' }}>{sym}{invoiceTotal.toFixed(2)}</span>
                  </div>
                )}

                {/* Notes */}
                <div className="input-group">
                  <label className="input-label">Notes</label>
                  <textarea className="input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
                    placeholder="Payment terms, additional info..." disabled={viewOnly} />
                </div>
              </>
            )}

            {activeTab === 'installments' && (
              <>
                {/* Edit mode: installment editor */}
                {!viewOnly && (
                  <div className="input-group">
                    <label className="input-label">Payment Installments</label>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                      Split the invoice total into scheduled payments. Clients and admins get reminders 7 days and 2 days before each due date.
                    </p>
                    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
                      <InstallmentsEditor
                        installments={form.installments}
                        onChange={v => set('installments', v)}
                        readOnly={false}
                        currency={form.currency}
                        invoiceTotal={itemsTotal}
                      />
                    </div>
                  </div>
                )}

                {/* View mode: installment status with pay buttons */}
                {viewOnly && form.installments.length > 0 && (
                  <InstallmentList
                    installments={form.installments}
                    invoiceId={initial?.id}
                    currency={form.currency}
                    canManage={canManage}
                    onPaid={onSave}
                  />
                )}

                {viewOnly && form.installments.length === 0 && (
                  <div className="empty-state" style={{ padding: 40 }}>
                    <CreditCard size={36} />
                    <p>No payment plan for this invoice</p>
                  </div>
                )}
              </>
            )}

            {!viewOnly && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary">
                  {isEdit ? 'Save Changes' : 'Create Invoice'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

// âââ Main Finance Page âââââââââââââââââââââââââââââââââââââââââââââ
const Finance = () => {
  const { user } = useAuth();
  const canManage = ['admin', 'manager'].includes(user?.role);
  const isClient = user?.role === 'client';

  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    try {
      const [inv, cli, proj] = await Promise.all([
        axios.get('/api/invoices').catch(() => ({ data: { data: [] } })),
        canManage ? axios.get('/api/clients').catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } }),
        canManage ? axios.get('/api/projects').catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } }),
      ]);
      setInvoices(inv.data.data || []);
      setClients(cli.data.data || []);
      setProjects(proj.data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoiceNumber}?`)) return;
    try { await axios.delete(`/api/invoices/${inv.id}`); load(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to delete'); }
  };

  const printInvoice = (inv) => {
    const items = (inv.items || []).map(item => `
      <tr><td>${item.description || ''}</td><td>${item.qty || 0}</td><td>${fmtCurrency(item.unitPrice, inv.currency)}</td><td>${fmtCurrency((item.qty || 0) * (item.unitPrice || 0), inv.currency)}</td></tr>
    `).join('');
    const installments = (inv.installments || []).map(inst => `
      <tr><td>${inst.description || ''}</td><td>${inst.dueDate || ''}</td><td>${fmtCurrency(inst.amount, inv.currency)}</td><td>${inst.status}</td></tr>
    `).join('');
    const html = `<!doctype html><html><head><title>${inv.invoiceNumber}</title><style>body{font-family:Arial,sans-serif;padding:32px;color:#111}h1{margin:0 0 8px}table{width:100%;border-collapse:collapse;margin-top:18px}th,td{border:1px solid #ddd;padding:9px;text-align:left}th{background:#f4f4f4}.total{text-align:right;font-size:18px;font-weight:700;margin-top:18px}</style></head><body><h1>${inv.invoiceNumber}</h1><p><strong>Client:</strong> ${inv.client?.companyName || ''}</p><p><strong>Project:</strong> ${inv.project?.name || '-'}</p><p><strong>Status:</strong> ${inv.status}</p><p><strong>Issue:</strong> ${inv.issueDate || ''} | <strong>Due:</strong> ${inv.dueDate || ''}</p>${items ? `<h3>Line Items</h3><table><thead><tr><th>Description</th><th>Qty</th><th>Unit</th><th>Total</th></tr></thead><tbody>${items}</tbody></table>` : ''}${installments ? `<h3>Installments</h3><table><thead><tr><th>Description</th><th>Due Date</th><th>Amount</th><th>Status</th></tr></thead><tbody>${installments}</tbody></table>` : ''}<div class="total">Total: ${fmtCurrency(inv.total, inv.currency)}</div><script>window.print()</script></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  // Stats
  const stats = {
    paid: groupedMoney(invoices, 'paid'),
    outstanding: groupedMoney(invoices, 'sent'),
    overdue: groupedMoney(invoices, 'overdue'),
    draft: invoices.filter(i => i.status === 'draft').length,
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const exportCsv = () => {
    const rows = [
      ['Invoice #', 'Client', 'Project', 'Status', 'Currency', 'Issue Date', 'Due Date', 'Total'],
      ...filtered.map(inv => [
        inv.invoiceNumber || '',
        inv.client?.companyName || '',
        inv.project?.name || '',
        inv.status || '',
        inv.currency || '',
        inv.issueDate || '',
        inv.dueDate || '',
        inv.total || 0,
      ]),
    ];
    const csv = rows.map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = invoices
    .filter(i => filterStatus === 'all' || i.status === filterStatus)
    .filter(i => filterClient === 'all' || i.clientId === filterClient)
    .filter(i => filterCurrency === 'all' || i.currency === filterCurrency)
    .filter(i => {
      if (!search) return true;
      const q = search.toLowerCase();
      return i.invoiceNumber?.toLowerCase().includes(q) || i.client?.companyName?.toLowerCase().includes(q) || i.project?.name?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let va, vb;
      if (sortBy === 'amount') { va = parseFloat(a.total); vb = parseFloat(b.total); }
      else if (sortBy === 'status') { va = a.status; vb = b.status; }
      else { va = new Date(a.createdAt); vb = new Date(b.createdAt); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const SortIcon = ({ col }) => sortBy === col ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : null;

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div className="page-content">
      {modal && (
        <InvoiceModal
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
          clients={clients}
          projects={projects}
          initial={modal.invoice}
          viewOnly={modal.viewOnly || isClient}
          canManage={canManage}
        />
      )}

      {/* Header */}
      <div className="flex-between mb-24">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Finance</h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</p>
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={() => setModal({})}>
            <Plus size={16} /> New Invoice
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        {[
          { label: 'Total Collected', value: stats.paid, icon: <CheckCircle size={22} />, color: '#22c55e' },
          { label: 'Outstanding', value: stats.outstanding, icon: <Clock size={22} />, color: '#3b82f6' },
          { label: 'Overdue', value: stats.overdue, icon: <AlertCircle size={22} />, color: '#ef4444' },
          { label: 'Drafts', value: stats.draft, icon: <FileText size={22} />, color: '#a0a0a0' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-icon" style={{ color: s.color }}>{s.icon}</div>
            <div className="stat-value" style={{ fontSize: 18 }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="search-bar">
        <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="input" style={{ paddingLeft: 36 }} placeholder="Search invoices..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canManage && (
            <select className="input select" style={{ width: 180 }} value={filterClient} onChange={e => setFilterClient(e.target.value)}>
              <option value="all">All clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
            </select>
          )}
          <select className="input select" style={{ width: 130 }} value={filterCurrency} onChange={e => setFilterCurrency(e.target.value)}>
            <option value="all">All currency</option>
            {Object.keys(CURRENCIES).map(code => <option key={code} value={code}>{code}</option>)}
          </select>
          {['all', 'draft', 'sent', 'paid', 'overdue', 'cancelled'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} className={`btn btn-sm ${filterStatus === s ? 'btn-primary' : 'btn-secondary'}`}>
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <button className="btn btn-secondary btn-sm" onClick={exportCsv}>
            <FileText size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      {!filtered.length ? (
        <div className="empty-state"><DollarSign size={40} /><p>No invoices found</p></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="finance-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Invoice #</th>
                  {!isClient && <th>Client</th>}
                  <th>Project</th>
                  <th onClick={() => toggleSort('date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Issue Date <SortIcon col="date" /></span>
                  </th>
                  <th>Due Date</th>
                  <th onClick={() => toggleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Status <SortIcon col="status" /></span>
                  </th>
                  <th>Plan</th>
                  <th onClick={() => toggleSort('amount')} style={{ cursor: 'pointer', userSelect: 'none', textAlign: 'right' }}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>Total <SortIcon col="amount" /></span>
                  </th>
                  {canManage && <th style={{ width: 80 }}></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => {
                  const instArr = Array.isArray(inv.installments) ? inv.installments : [];
                  const hasInst = instArr.length > 0;
                  const paidInst = hasInst ? instArr.filter(i => i.status === 'paid').length : 0;
                  const totalInst = hasInst ? instArr.length : 0;
                  const isExpanded = expandedId === inv.id;
                  const sym = CURRENCIES[inv.currency]?.symbol || '$';

                  return (
                    <React.Fragment key={inv.id}>
                      <tr
                        onClick={() => setModal({ invoice: inv, viewOnly: true })}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        {/* Expand toggle (only if has installments) */}
                        <td onClick={e => { e.stopPropagation(); hasInst && setExpandedId(isExpanded ? null : inv.id); }}>
                          {hasInst && (
                            <div style={{ color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--accent)' }}>{inv.invoiceNumber}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{CURRENCIES[inv.currency]?.flag} {inv.currency}</div>
                        </td>
                        {!isClient && <td style={{ fontSize: 13 }}>{inv.client?.companyName || ''}</td>}
                        <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{inv.project?.name || ''}</td>
                        <td style={{ fontSize: 13 }}>{inv.issueDate ? new Date(inv.issueDate).toLocaleDateString() : ''}</td>
                        <td style={{ fontSize: 13 }}>
                          <span style={{ color: inv.dueDate && new Date(inv.dueDate) < new Date() && inv.status !== 'paid' ? '#ef4444' : 'inherit' }}>
                            {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : ''}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${statusColor[inv.status]}`}>
                            {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                          </span>
                        </td>
                        <td>
                          {hasInst ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 48, height: 5, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                                <div style={{ height: '100%', background: '#22c55e', width: `${(paidInst / totalInst) * 100}%`, borderRadius: 3 }} />
                              </div>
                              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{paidInst}/{totalInst}</span>
                            </div>
                          ) : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}></span>}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
                          {sym}{Number(inv.total).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        {canManage && (
                          <td onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                              <button onClick={() => setModal({ invoice: inv })} title="Edit"
                                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px 6px' }}>
                                <Pencil size={13} />
                              </button>
                              <button onClick={() => printInvoice(inv)} title="Print invoice"
                                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px 6px' }}>
                                <FileText size={13} />
                              </button>
                              <button onClick={() => deleteInvoice(inv)} title="Delete"
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px 6px' }}>
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>

                      {/* Expanded installments row */}
                      {isExpanded && hasInst && (
                        <tr>
                          <td colSpan={canManage ? (isClient ? 8 : 9) : (isClient ? 7 : 8)}
                            style={{ background: 'var(--bg-secondary)', padding: '16px 24px' }}>
                            <InstallmentList
                              installments={instArr}
                              invoiceId={inv.id}
                              currency={inv.currency}
                              canManage={canManage}
                              onPaid={() => load()}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)' }}>
                  <td colSpan={canManage ? (isClient ? 7 : 8) : (isClient ? 6 : 7)}
                    style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>
                    {filtered.length} invoice{filtered.length !== 1 ? 's' : ''}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, fontSize: 15 }}>
                    
                  </td>
                  {canManage && <td />}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Finance;
